import { parsePreferredLocale, parseWebUserAgent, WEB_DEVICE_ID_COOKIE_NAME, WEB_TIMEZONE_COOKIE_NAME } from '~/shared/utils/web-meta';

export function getDeviceId() {
  const storageKey = 'pgx_device_id';

  let deviceId: string | null = null;

  if (typeof window !== 'undefined') {
    try {
      deviceId = localStorage.getItem(storageKey);
    } catch (error) {
      console.warn('localStorage unavailable, falling back to cookies for device ID.', error);
    }
  }

  if (!deviceId) {
    deviceId = getCookieValue(WEB_DEVICE_ID_COOKIE_NAME);
  }

  if (!deviceId) {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      deviceId = crypto.randomUUID().toUpperCase();
    } else {
      console.warn('crypto.randomUUID() not available. Using fallback for device ID.');
      deviceId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`.toUpperCase();
    }

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, deviceId);
      } catch (error) {
        console.warn('localStorage unavailable, skipping device ID persistence.', error);
      }
    }
  } else {
    deviceId = deviceId.toUpperCase();
  }

  return deviceId;
}

/**
 * Syncs device ID and timezone to cookies so server-side code can access them.
 *
 * Call this once on app initialization (e.g., in root.tsx useEffect).
 * The cookies are used by `createRequestMeta` in Remix actions to properly
 * attribute changes to the correct device.
 *
 * @example
 * ```tsx
 * // In root.tsx
 * useEffect(() => {
 *   syncMetaCookies();
 * }, []);
 * ```
 */
export function syncMetaCookies(): void {
  if (typeof document === 'undefined') return;

  const deviceId = getDeviceId();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Set cookies with 1 year expiry, accessible to all paths
  const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds

  // Device ID cookie
  document.cookie = `${WEB_DEVICE_ID_COOKIE_NAME}=${deviceId}; path=/; max-age=${maxAge}; SameSite=Lax`;

  // Timezone cookie (URL encoded for safety with special chars like /)
  document.cookie = `${WEB_TIMEZONE_COOKIE_NAME}=${encodeURIComponent(timezone)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}

export function getDeviceInfo(): {
  device: string;
  os: string;
  osVersion: string;
  language: string;
  locale: string;
  timezone: string;
  timezoneName: string;
  timezoneIANA: string;
} {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browserLocale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const { device, os, osVersion } = parseWebUserAgent(userAgent);
  const { language, locale } = parsePreferredLocale(browserLocale);

  let timezone = '+00:00';
  let timezoneName = 'UTC';
  let timezoneIANA = 'UTC';

  if (typeof Intl !== 'undefined' && typeof Date !== 'undefined') {
    const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
    timezoneIANA = resolvedOptions.timeZone || 'UTC';

    try {
      timezoneName =
        Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
          .formatToParts(new Date())
          .find(part => part.type === 'timeZoneName')?.value || timezoneIANA;
    } catch (e) {
      console.warn('Could not determine short timezone name, using IANA name as fallback:', e);
      timezoneName = timezoneIANA;
    }

    // get offset from UTC in format +00:00
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
    const offsetMinutesPart = Math.abs(offsetMinutes % 60);
    const offsetSign = offsetMinutes <= 0 ? '+' : '-';
    timezone = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutesPart).padStart(2, '0')}`;
  }

  return {
    device,
    os,
    osVersion,
    language,
    locale,
    timezone,
    timezoneName,
    timezoneIANA,
  };
}
